// Incremental SSE parser. Frames and UTF-8 characters may span network chunks.
export function createSseParser(onEvent) {
  let line = '';
  let data = [];
  let type = 'message';
  let id;
  let skipLf = false;
  const consumeLine = () => {
    if (line === '') {
      if (data.length) onEvent({ type, id, data: data.join('\n') });
      data = [];
      type = 'message';
      id = undefined;
    } else if (!line.startsWith(':')) {
      const colon = line.indexOf(':');
      const field = colon < 0 ? line : line.slice(0, colon);
      let value = colon < 0 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'data') data.push(value);
      if (field === 'event') type = value;
      if (field === 'id' && !value.includes('\0')) id = value;
    }
    line = '';
  };
  return (chunk) => {
    for (const char of chunk) {
      if (skipLf && char === '\n') { skipLf = false; continue; }
      skipLf = false;
      if (char === '\r' || char === '\n') {
        consumeLine();
        skipLf = char === '\r';
      } else {
        line += char;
        if (line.length > 1000000) throw new Error('Oversized event frame');
      }
    }
  };
}

export function subscribeJobStream(url, { tenantId, onChange, onStatus, fetchImpl = fetch }) {
  const controller = new AbortController();
  let cursor;
  let retry;
  let delay = 1000;
  let stopped = false;
  const status = (value) => { if (!stopped) onStatus(value); };
  const connect = async () => {
    let reader;
    try {
      status(cursor === undefined ? 'connecting' : 'reconnecting');
      const headers = { Accept: 'text/event-stream' };
      if (tenantId) headers['X-Tenant-Id'] = tenantId;
      if (cursor !== undefined) headers['Last-Event-ID'] = cursor;
      const response = await fetchImpl(url, { headers, credentials: 'include', signal: controller.signal });
      if ([401, 403, 404].includes(response.status)) {
        status('unavailable');
        stopped = true;
        return;
      }
      if (!response.ok || !response.body || !response.headers.get('content-type')?.includes('text/event-stream')) {
        throw new Error('Live connection unavailable');
      }
      status('live');
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parse = createSseParser(({ type, id, data }) => {
        if (stopped) return;
        if (type === 'access_lost') {
          status('unavailable');
          stopped = true;
          controller.abort();
          return;
        }
        if (!['snapshot', 'reset', 'job_event', 'changed'].includes(type)) return;
        const value = JSON.parse(data);
        // Reset/snapshot are authoritative even when a cursor moves backwards.
        if (type === 'job_event' && cursor !== undefined && Number(id) <= Number(cursor)) return;
        onChange(value);
        if (id !== undefined) cursor = id;
        delay = 1000;
      });
      while (!stopped) {
        const { value, done } = await reader.read();
        if (done) break;
        parse(decoder.decode(value, { stream: true }));
      }
    } catch (error) {
      if (controller.signal.aborted) return;
    } finally {
      if (reader) { await reader.cancel().catch(() => {}); reader.releaseLock(); }
    }
    if (!stopped) {
      status('reconnecting');
      retry = setTimeout(connect, delay + Math.random() * delay * 0.2);
      delay = Math.min(delay * 2, 30000);
    }
  };
  connect();
  return () => { stopped = true; clearTimeout(retry); controller.abort(); };
}
